/* eslint-disable @typescript-eslint/naming-convention */
import type { ToggleOptionBlock } from '@ps/web-martech-cms-types/Form';
import type { ValidatorTranslation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback } from 'react';

import { Validation } from '@swf/components/Inputs/Input';
import Text from '@swf/components/Inputs/Text';
import Toggle from '@swf/components/Inputs/Toggle';

import { getErrorMessage } from '../../utils';

import { TXTCLI_Required } from './SingleToggle.messages.json';
import styles from './SingleToggle.scss';

export type ToggleType = 'checkbox' | 'radio';
interface Props {
  type: ToggleType;
  requiredValidation: {
    active: boolean;
    analytics_tracking_id?: string;
    error_message?: string;
  };
  field_name: string;
  input: ToggleOptionBlock;
  fieldValue: string;
}

const atLeastOneRequired = (message?: ValidatorTranslation) => (value: []) => ({
  valid: !!value?.length,
  message
});

const minLength = (min: number, message?: ValidatorTranslation) => (value: string) => ({
  valid: !value || value?.length >= min,
  message
});

const maxLength = (max: number, message?: ValidatorTranslation) => (value: string) => ({
  valid: !value || value?.length <= max,
  message
});

const SingleToggle: React.FC<Props> = ({
  type,
  field_name,
  input,
  fieldValue,
  requiredValidation
}) => {
  const {
    value,
    label,
    with_text_area: { show_text_area, min_length_validation, max_length_validation }
  } = input;
  const inputFieldName = `${field_name}_${value.toLowerCase()}`;
  const disabled = fieldValue !== value;

  const getTextAreaValidators = useCallback(() => {
    const validators = [];
    if (min_length_validation.min_length) {
      const errorMessage = getErrorMessage(min_length_validation);
      validators.push(minLength(min_length_validation.min_length, errorMessage));
    }
    if (max_length_validation.max_length) {
      const errorMessage = getErrorMessage(max_length_validation);
      validators.push(maxLength(max_length_validation.max_length, errorMessage));
    }
    return validators;
  }, [min_length_validation, max_length_validation]);

  return (
    <div className={classNames({ [styles.withTextArea]: show_text_area })}>
      <Toggle
        id={`${field_name}-${value}`}
        type={type as ToggleType}
        name={field_name}
        label={label}
        value={value}
        validators={
          requiredValidation.active
            ? [atLeastOneRequired(getErrorMessage(requiredValidation, TXTCLI_Required))]
            : []
        }
      />
      {show_text_area && (
        <Text
          className={classNames(styles.textArea)}
          hideLabel
          autoComplete="false"
          id={inputFieldName}
          label={inputFieldName}
          showValidationTick={!disabled}
          disabled={disabled}
          type="textarea"
          validationVariant={
            fieldValue === value ? Validation.ImmediateInline : Validation.Invisible
          }
          validators={getTextAreaValidators()}
        />
      )}
    </div>
  );
};

export default SingleToggle;
